// Communication preferences hooks (ART-285) — the WRAPPER layer. The email + push switches, two doors: the signed-in
// person (`/me/communication-preferences`, under ['me', …]) and the token in a notification email's footer
// (`/public/communication-preferences`, which works signed out). The token rides the request BODY, never a query string.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { ApiError } from "./apiError.js";

export type CommunicationPreferences = components["schemas"]["CommunicationPreferences"];
export type CommunicationPreferenceChange = components["schemas"]["CommunicationPreferenceChange"];

const MINE = ["me", "communication-preferences"] as const;

/** The signed-in person's switches. */
export function useMyCommunicationPreferences() {
  const { api } = useApp();
  return useQuery({
    queryKey: MINE,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/communication-preferences");
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  });
}

export function useSaveMyCommunicationPreferences() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (changes: CommunicationPreferenceChange[]) => {
      const { data, error, response } = await api.PUT("/api/v1/me/communication-preferences", { body: { changes } });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: (data) => qc.setQueryData(MINE, data),
  });
}

/** The switches reached from an email's footer link. A bad or missing token is a 404, surfaced as an error. */
export function useTokenCommunicationPreferences(token: string) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["public", "communication-preferences", token],
    enabled: token.length > 0,
    retry: false,
    queryFn: async () => {
      const { data, error, response } = await api.POST("/api/v1/public/communication-preferences/read", { body: { token } });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  });
}

export function useSaveTokenCommunicationPreferences(token: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (changes: CommunicationPreferenceChange[]) => {
      const { data, error, response } = await api.PUT("/api/v1/public/communication-preferences", {
        body: { token, changes },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: (data) => qc.setQueryData(["public", "communication-preferences", token], data),
  });
}
